import { filterXSS } from "xss";
import { decode } from "he";

/**
 * On paste events for the editors
 * 1. remove zero-width spaces and replace other special spaces with " "
 * 2. split the string by newlines
 * 3. preserve allowed HTML tags, wrap plain text in p tags
 * @param input - The input string to format
 * @returns The formatted string
 */
export const formatPastedContentAgain = (input: string, isHtml: boolean): string => {
    const SPACE_REGEX =
        /(\xA0|&ensp;|\u2002|&emsp;|\u2003|&thinsp;|\u2009|\u200A)/g;
    const ZERO_WIDTH_REGEX = /(\u200B|&#xfeff;)/g;
    const LINE_BREAK_REGEX = /\r\n|\r|\n/g;

    const result = input
        .replace(ZERO_WIDTH_REGEX, "")
        .replace(SPACE_REGEX, " ");

    if (!isHtml) {
        return result;
    }

    // Sanitize HTML once before processing lines
    const cleanHtml = filterXSS(result, {
        whiteList: {
            p: [],
            strong: [],
            em: [],
            u: [],
            h1: [],
            h2: [],
            h3: [],
            h4: [],
            h5: [],
            h6: [],
            ul: [],
            ol: [],
            li: [],
            a: ['href']
        },
        stripIgnoreTag: true,
        stripIgnoreTagBody: ["script", "style"]
    });

    const lines = cleanHtml.split(LINE_BREAK_REGEX);

    const linesWithParagraphs = lines
        .filter(line => line.trim().length > 0)
        .map(line => {
            const trimmedLine = line.trim();
            const parser = new DOMParser();
            const doc = parser.parseFromString(trimmedLine, "text/html");

            // Check if the line already contains HTML elements
            const hasHtmlContent = doc.body.children.length > 0;

            if (hasHtmlContent) {
                // Find all list items
                const listItems = doc.querySelectorAll("li");

                listItems.forEach(li => {
                    const paragraphs = li.querySelectorAll("p");

                    // If there's more than one paragraph and at least one has content
                    if (paragraphs.length > 0) {
                        let hasContentParagraph = false;

                        // Check if any paragraph has content
                        paragraphs.forEach(p => {
                            if (p.textContent?.trim()) {
                                hasContentParagraph = true;
                            }
                        });

                        // If we found a content paragraph, remove all empty ones
                        if (hasContentParagraph) {
                            paragraphs.forEach(p => {
                                if (!p.textContent?.trim()) {
                                    p.remove();
                                }
                            });
                        }
                    }
                });

                return doc.body.innerHTML;
            }

            return `<p>${trimmedLine}</p>`;
        });

    return linesWithParagraphs.join("");
};

export const normalizeReadableSpaces = (input: string): string => {
    return input
        .replace(/[\u200B-\u200D\uFEFF]/g, "") // zero-width
        .replace(/\u00A0|\u1680|\u180E|[\u2000-\u200A]|\u202F|\u205F|\u3000/g, " ") // exotic spaces
        .replace(/[ \t]+/g, " ") // collapse duplicate spaces/tabs
        .trim();
};

/**
 * Handles plain text paste formatting.
 * - Converts markdown-style bullet points (-, *, •) to <ul><li>
 * - Wraps remaining lines in <p>
 * - Ensures all lists are flat (no nested structure)
 * - Removes zero-width and special space characters
 */
export const cleanPastedText = (input: string): string => {
    // Sanitize special spaces and zero-width characters
    const decoded = decode(input);
    const sanitized = normalizeReadableSpaces(decoded);

    // Split input into non-empty trimmed lines
    const lines = sanitized.split(/\r?\n/).map(line => line.trim()).filter(Boolean);

    // Match top-level bullets only
    const isBullet = (line: string) => /^\s*[-*•]\s+/.test(line);

    let html = "";
    let inList = false;

    lines.forEach((line, index) => {
        if (isBullet(line)) {
            if (!inList) {
                html += "<ul>";
                inList = true;
            }

            const content = line.replace(/^\s*([-*•])\s+/, ""); // remove bullet symbol and leading spaces
            html += `<li>${content}</li>`;

            const nextLine = lines[index + 1] ?? "";
            if (!isBullet(nextLine)) {
                html += "</ul>";
                inList = false;
            }
        } else {
            if (inList) {
                html += "</ul>";
                inList = false;
            }
            html += `<p>${line}</p>`;
        }
    });

    if (inList) html += "</ul>";

    return html;
};

// Define allowed HTML tags and attributes
const whiteList = {
    p: [],
    strong: [],
    em: [],
    u: [],
    h1: [],
    h2: [],
    h3: [],
    h4: [],
    ul: [],
    ol: [],
    li: [],
    a: ["href"]
};

const bulletChars = /^[•‣▪–—\u2022\u2023\u25AA\u25CF\u25E6]\s*/;

/**
 * Cleans and normalizes pasted HTML in a browser-safe way.
 * - Sanitizes HTML input to remove scripts/styles and disallowed tags
 * - Fixes common structural issues like:
 *   - <p><ul>...</ul></p>
 *   - <li><p>Text</p></li>
 *   - <span><ul>...</ul></span>
 *   - Flattening nested list items from sources like ChatGPT
 * - Wraps top-level text nodes in <p> tags
 */
export const cleanPastedHtml = (rawHtml: string): string => {
    // Step 1: Sanitize HTML to remove scripts, styles, and disallowed tags
    const sanitized = filterXSS(rawHtml, {
        whiteList,
        stripIgnoreTag: true,
        stripIgnoreTagBody: ["style", "script"]
    });

    // Step 2: Parse into DOM for manipulation
    const div = document.createElement("div");
    div.innerHTML = sanitized;

    // ✅ STEP 1: Strip known bullet characters from <p> content (from PDF/Word pastes)
    div.querySelectorAll("p").forEach((p) => {
        const text = p.textContent?.trim();
        if (text && bulletChars.test(text)) {
            p.textContent = text.replace(bulletChars, "");
        }
    });

    // Step 3: Wrap top-level text nodes in <p> tags
    Array.from(div.childNodes).forEach(node => {
        if (node.nodeType === 3 && node.textContent?.trim()) {
            const lines = node.textContent
                .split(/\r?\n/)
                .map(line => line.trim())
                .filter(Boolean);

            const fragment = document.createDocumentFragment();
            lines.forEach(line => {
                const p = document.createElement("p");
                p.textContent = line;
                fragment.appendChild(p);
            });

            div.replaceChild(fragment, node);
        }
    });

    // Step 4: Unwrap cases like <p><ul>...</ul></p> → <ul>...</ul>
    div.querySelectorAll("p").forEach(p => {
        const onlyChild = p.firstElementChild;
        if (onlyChild && ["UL", "OL"].includes(onlyChild.tagName)) {
            p.replaceWith(onlyChild);
        }
    });

    // Step 5: Same unwrap logic for <span><ul>...</ul></span>
    div.querySelectorAll("span").forEach(span => {
        const onlyChild = span.firstElementChild;
        if (onlyChild && ["UL", "OL"].includes(onlyChild.tagName)) {
            span.replaceWith(onlyChild);
        }
    });

    // Step 6: Flatten nested lists inside <li> elements
    div.querySelectorAll("li").forEach(li => {
        const nestedLists = li.querySelectorAll("ul, ol");

        nestedLists.forEach(nestedList => {
            const parentList = li.closest("ul, ol");
            if (!parentList) return;

            // Move each nested <li> directly after its parent <li>
            const listItems = Array.from(nestedList.querySelectorAll("li"));
            let referenceNode = li;

            listItems.forEach(nestedLi => {
                // If <li> wraps a single <p>, extract the text
                const nestedP = nestedLi.querySelector("p");
                if (nestedP && nestedLi.children.length === 1) {
                    nestedLi.innerHTML = nestedP.innerHTML;
                }

                referenceNode.insertAdjacentElement("afterend", nestedLi);
                referenceNode = nestedLi;
            });

            nestedList.remove();
        });
    });

    // Step 7: Normalize <li> content so it's wrapped in <p> only if necessary
    div.querySelectorAll("li").forEach(li => {
        // Wrap in <p> if the <li> contains only text
        if (li.childElementCount === 0 && li.textContent?.trim()) {
            const text = li.textContent.trim();
            li.innerHTML = `<p>${text}</p>`;
        }
        // If there's one element but it's not a <p>, wrap it
        else if (li.childElementCount === 1 && li.firstElementChild?.tagName !== "P") {
            const text = li.innerHTML;
            li.innerHTML = "";
            const p = document.createElement("p");
            p.innerHTML = text;
            li.appendChild(p);
        }
    });

    // Step 8: Remove any empty <p> tags left behind
    div.querySelectorAll("p").forEach(p => {
        if (!p.textContent?.trim() && p.children.length === 0) {
            p.remove();
        }
    });

    // Final output: cleaned HTML string
    return div.innerHTML;
};

/**
 * Handles all paste events (HTML and plain text)
 * Cleans, sanitizes, and formats pasted content
 */
export const formatPastedContent = (input: string, isHtml: boolean): string => {
    const trimmed = input.trim();
    const maybeHtml = /^<[^>]+>/.test(trimmed); // crude HTML check
    if (maybeHtml && isHtml) {
        return cleanPastedHtml(input);
    }

    return cleanPastedText(input);
};
